import { asTeammateId } from "@repo/app-types/entities/teammate";
import { Size } from "@repo/ui/helpers/helpers";
import { Input } from "@repo/ui/Inputs/input";
import { Textarea } from "@repo/ui/Inputs/textarea";
import { Label } from "@repo/ui/Label/label";
import { IconBook2, IconInfoCircle } from "@tabler/icons-react";
import type { FC } from "react";
import { useParams } from "react-router";
import { FullHeightPageContent } from "../../components/layout/pageLayout.js";
import { PlaybookEditorContainer } from "../../components/playbookEditorContainer.js";
import { AgentConfigAssistantSidebar } from "./agentConfigAssistantSidebar.js";
import { AgentConfigToolbar } from "./agentConfigToolbar.js";
import { AgentPhoneNumberSection } from "./agentPhoneNumberSection.js";
import { AgentSimulationSlideover } from "./agentSimulationSlideover.js";
import { AttachmentEditorContainer } from "./components/attachmentEditorContainer.js";
import { KnowledgeBaseSection } from "./components/knowledgeBaseSection.js";
import { ToolsSection } from "./components/toolsSection.js";
import { TeammateAgentDataExtractionFieldsSection } from "./teammateAgentDataExtractionFieldsSection.js";
import { useTeammateAgentConfigController } from "./useTeammateAgentConfigController.js";

export const TeammateAgentConfigurationTab: FC = () => {
  const { teammateAgentId: teammateAgentIdParam } = useParams();
  if (!teammateAgentIdParam) {
    throw new Error("Missing params.");
  }
  const teammateAgentId = asTeammateId(teammateAgentIdParam);

  const controller = useTeammateAgentConfigController(teammateAgentId);
  const {
    playbookId,
    isStaticAgent,
    agentConfig,
    playbook,
    allKnowledgeBases,
    isActionInProgress,
    editingAttachment,
    setEditingAttachment,
    pendingPatch,
    setPendingPatch,
    handleDataExtractionFieldsChange,
  } = controller;

  const agentConfigData = agentConfig.data;
  const agentConfigUnpublishedChanges = agentConfig.unpublishedChanges;
  const agentConfigValidationErrors = agentConfig.validationErrors;
  const onAgentConfigChange = agentConfig.onChange;

  const playbookData = playbook.data;
  const onPlaybookChange = playbook.onChange;

  const sidebar = <AgentConfigAssistantSidebar controller={controller} />;

  const configContent = (
    <div className="flex min-w-0 flex-1 flex-col overflow-hidden bg-white">
      <div className="relative h-full overflow-y-auto">
        <div className="space-y-6 p-6">
          {isStaticAgent ? (
            <div className="flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800">
              <IconInfoCircle className="mt-0.5 h-4 w-4 shrink-0 text-blue-500" />
              <span>
                This agent is managed by Alpharun. Contact Alpharun support if
                you want to modify it.
              </span>
            </div>
          ) : null}
          <AgentPhoneNumberSection phoneNumber={controller.phoneNumber} />
          <div className="grid gap-4 sm:grid-cols-5">
            <div className="sm:col-span-1">
              <Label size={Size.SMALL} className="mb-1.5">
                <span className="flex items-center gap-1.5">
                  Agent name
                  {agentConfigUnpublishedChanges.identityNameChanged && (
                    <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                  )}
                </span>
              </Label>
              <Input
                value={agentConfigData?.identityName ?? ""}
                onChange={(event) =>
                  onAgentConfigChange({
                    identityName: event.target.value,
                  })
                }
                placeholder="Sarah"
                errorDescription={
                  agentConfigValidationErrors.identityName ?? undefined
                }
                maxLength={100}
                isDisabled={isStaticAgent}
              />
            </div>
            <div className="sm:col-span-4">
              <Label size={Size.SMALL} className="mb-1.5">
                <span className="flex items-center gap-1.5">
                  Agent role
                  {agentConfigUnpublishedChanges.identityRoleChanged && (
                    <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                  )}
                </span>
              </Label>
              <Textarea
                id="agent-role"
                value={agentConfigData?.identityRole ?? ""}
                onChange={(value) =>
                  onAgentConfigChange({ identityRole: value })
                }
                placeholder="Representative for Acme, a bluetooth headphones manufacturer, responsible for qualifying leads."
                maxLength={500}
                errorDescription={
                  agentConfigValidationErrors.identityRole ?? undefined
                }
                isDisabled={isStaticAgent}
              />
            </div>

            <div className="col-span-full">
              <Label size={Size.SMALL} className="mb-1.5">
                <span className="flex items-center gap-1.5">
                  Customer description
                  {agentConfigUnpublishedChanges.identityAdditionalContextChanged && (
                    <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                  )}
                </span>
              </Label>
              <Textarea
                id="customer-role"
                value={agentConfigData?.identityAdditionalContext ?? ""}
                onChange={(newValue) =>
                  onAgentConfigChange({
                    identityAdditionalContext: newValue,
                  })
                }
                placeholder="A potential customer calling the phone number on our website."
                maxLength={500}
                errorDescription={
                  agentConfigValidationErrors.identityAdditionalContext ??
                  undefined
                }
                isDisabled={isStaticAgent}
              />
            </div>
          </div>

          <ToolsSection
            toolIds={agentConfigData?.toolIds ?? []}
            hasUnpublishedChanges={agentConfigUnpublishedChanges.toolIdsChanged}
            onToolIdsChange={(newToolIds) => {
              onAgentConfigChange({
                toolIds: newToolIds,
              });
            }}
            onPublish={controller.onPublish}
            isDisabled={isActionInProgress}
          />

          {playbookId ? (
            <TeammateAgentDataExtractionFieldsSection
              playbookId={playbookId}
              fieldIds={playbookData?.gradingDataExtractionFieldIds ?? []}
              onChange={handleDataExtractionFieldsChange}
              isDisabled={isActionInProgress}
            />
          ) : null}
        </div>

        {/* Knowledge Base */}
        <div className="px-6 pb-6">
          <Label size={Size.SMALL} className="mb-2">
            Knowledge base
          </Label>
          <KnowledgeBaseSection
            knowledgeBaseIds={playbookData?.knowledgeBaseIds ?? []}
            allKnowledgeBases={allKnowledgeBases ?? []}
            onKnowledgeBaseIdsChange={(ids) =>
              onPlaybookChange({ knowledgeBaseIds: ids })
            }
            onEditAttachment={(knowledgeBaseId, attachmentId) =>
              setEditingAttachment({ knowledgeBaseId, attachmentId })
            }
            banner={
              <div className="rounded-lg bg-gray-50 p-4">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 shrink-0">
                    <IconBook2 className="h-5 w-5 text-gray-400" />
                  </div>
                  <div className="text-sm text-gray-600">
                    <p>
                      Give your agent reference documents and information to
                      draw from during conversations, like FAQs, product
                      information, or company policies.
                    </p>
                  </div>
                </div>
              </div>
            }
          />
        </div>

        {/* Playbook Editor */}
        <div className="px-6 pb-6">
          <Label size={Size.SMALL} className="mb-2">
            Playbook
          </Label>
          <div className="flex min-h-0 flex-1 flex-col">
            <PlaybookEditorContainer
              data={playbookData}
              initialOutcome={playbook.initialData?.outcome}
              liveOutcome={playbook.liveData?.outcome}
              liveTree={playbook.liveData?.tree}
              onChange={onPlaybookChange}
              isReadOnly={isStaticAgent}
              playbookId={playbookId}
              isTranscriptOutcomeOnly
            />
          </div>
        </div>
      </div>
    </div>
  );

  return (
    // Declared full height so the page shell provides a bounded scroll region
    // this fills exactly; inner areas scroll instead of the page.
    <FullHeightPageContent>
      <div className="flex h-full min-w-[1100px] flex-col">
        {editingAttachment ? (
          <AttachmentEditorContainer
            key={editingAttachment.attachmentId}
            knowledgeBaseId={editingAttachment.knowledgeBaseId}
            attachmentId={editingAttachment.attachmentId}
            pendingPatch={pendingPatch}
            onPatchApplied={() => setPendingPatch(null)}
            onClose={() => {
              setEditingAttachment(null);
              setPendingPatch(null);
            }}
            sidebar={sidebar}
          />
        ) : (
          <>
            <AgentConfigToolbar controller={controller} />
            <div className="flex min-h-0 flex-1">
              {configContent}
              {sidebar}
            </div>
          </>
        )}

        <AgentSimulationSlideover controller={controller} />
      </div>
    </FullHeightPageContent>
  );
};
